import React from 'react';
import { createPortal } from 'react-dom';
import { WorkflowAssistantHeaderContext } from '../WorkflowAssistantHeader';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useDocumentAssistant } from './DocumentAssistantContext';
import { EditOnlyDocumentConversation } from './DocumentAssistantConversation';

import HomeChatPanel from './HomeChatPanel';
import type { GpcChatWorkflowStageKey } from '../GpcChat/types';
import type {
  HomeChatHandoffContext,
  HomeChatPageContext,
  WorkflowNavigationCheckpoint,
  WorkflowRecommendationType,
} from './types';

interface WorkflowChatCompanionProps {
  children: React.ReactNode;
  enabled?: boolean;
  authorized?: boolean;
  sessionId: string | null;
  visible: boolean;
  workflowName?: string;
  pageContext?: HomeChatPageContext;
  onClose?: () => void;
  onOpen?: () => void;
  userKey?: string;
  flowId?: string | null;
  /** Reset sidebar state after restoring its session without touching the workflow. */
  conversationRevision?: number;
  onSessionIdChange?: (sessionId: string | null, options?: import('./types').WorkflowChatSessionChangeOptions) => void;
  onTurnStart?: () => void;
  onOpenWorkflow?: (
    flowId: string,
    targetStageHint?: GpcChatWorkflowStageKey,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenMrWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenGeneratedServicesPwsWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => void;
  onOpenNavigationHandle?: (
    navigationHandle: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onOpenCreatedWorkflow?: (
    flowId: string,
    context?: HomeChatHandoffContext,
  ) => Promise<void>;
  onStartWorkflowType?: (
    workflowType: WorkflowRecommendationType,
    context?: HomeChatHandoffContext,
  ) => void;
  onBeforeWorkflowNavigation?: (
    previous?: WorkflowNavigationCheckpoint,
  ) => WorkflowNavigationCheckpoint | false;
}

export const WORKFLOW_CHAT_COMPANION_MIN_HEIGHT = {
  xs: 560,
  lg: 0,
} as const;

/**
 * Keeps the Home Chat conversation in the normal page layout while a workflow
 * is active. It intentionally uses no dialog, drawer, portal, or backdrop.
 */
const WorkflowChatCompanion: React.FC<WorkflowChatCompanionProps> = ({
  children,
  enabled = true,
  authorized = true,
  sessionId,
  visible,
  workflowName,
  pageContext,
  onClose,
  onOpen,
  userKey,
  flowId,
  conversationRevision = 0,
  onSessionIdChange,
  onTurnStart,
  onOpenWorkflow,
  onOpenMrWorkflow,
  onOpenGeneratedServicesPwsWorkflow,
  onOpenNavigationHandle,
  onOpenCreatedWorkflow,
  onStartWorkflowType,
  onBeforeWorkflowNavigation,
}) => {
  const workflowMainRef = React.useRef<HTMLElement | null>(null);
  const launcherRef = React.useRef<HTMLButtonElement | null>(null);
  const asideRef = React.useRef<HTMLElement | null>(null);
  const headerSlots = React.useRef(new Set<HTMLElement>());
  const [headerHost, setHeaderHost] = React.useState<HTMLElement | null>(null);
  const registerSlot = React.useCallback((slot: HTMLElement) => {
    const selectHost = () => setHeaderHost(Array.from(headerSlots.current).filter(element => element.isConnected).pop() || null);
    headerSlots.current.add(slot);
    selectHost();
    return () => { headerSlots.current.delete(slot); selectHost(); };
  }, []);
  const headerRegistry = React.useMemo(() => ({ registerSlot }), [registerSlot]);

  const assistant = useDocumentAssistant();
  const editAvailable = !!assistant.registration?.capability.activeEditor && assistant.registration.capability.canEdit;
  const assistantAvailable = (enabled || editAvailable) && (authorized || editAvailable);
  const preferenceKey = userKey && flowId ? `farchat:workflow-assistant:visibility:v1:${userKey}:${flowId}` : null;
  const readPreference = () => { try { const raw = preferenceKey ? localStorage.getItem(preferenceKey) : null; return raw === 'true' ? true : raw === 'false' ? false : null; } catch { return null; } };
  const [visibilityOverride, setVisibilityOverride] = React.useState<boolean | null>(readPreference);
  const shown = (visibilityOverride ?? visible) && (authorized || editAvailable) && (enabled || editAvailable);
  const setVisibility = (next: boolean) => {
    setVisibilityOverride(next);
    try { if (preferenceKey) localStorage.setItem(preferenceKey, String(next)); } catch { /* optional preference */ }
    if (next) onOpen?.(); else onClose?.();
  };
  React.useEffect(() => { setVisibilityOverride(readPreference()); }, [preferenceKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const lastOpenRevision = React.useRef(assistant.openRevision);
  React.useEffect(() => {
    if (lastOpenRevision.current === assistant.openRevision) return;
    lastOpenRevision.current = assistant.openRevision;
    setVisibility(true);
  }, [assistant.openRevision]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (shown) window.requestAnimationFrame(() => asideRef.current?.querySelector<HTMLTextAreaElement>('textarea')?.focus());
  }, [shown]);

  const handleCollapse = () => {
    setVisibility(false);
    window.requestAnimationFrame(() => (onOpen ? launcherRef.current : workflowMainRef.current)?.focus());
  };


  const toggle = <Tooltip title={shown ? 'Close assistant' : 'Open assistant'}>
    <IconButton ref={launcherRef} onClick={shown ? handleCollapse : () => setVisibility(true)} aria-label={shown ? 'Collapse workflow conversation' : 'Open workflow assistant'} aria-pressed={shown} aria-expanded={shown} aria-controls="workflow-assistant-sidebar" aria-busy={assistant.mutationPending || undefined} aria-description={assistant.mutationPending ? 'Applying changes' : undefined} data-tour-id={shown ? 'farchat-controls' : 'workflow-assistant-launcher'} sx={{ width: 40, height: 40, flexShrink: 0 }}>
      <ChatBubbleOutlineIcon />
    </IconButton>
  </Tooltip>;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: 'flex-start',
        minWidth: 0,
      }}
    >
      <WorkflowAssistantHeaderContext.Provider value={assistantAvailable ? headerRegistry : null}>
      <Box
        ref={workflowMainRef}
        component="main"
        tabIndex={-1}
        data-testid="workflow-chat-main"
        sx={{
          order: { xs: 2, lg: 1 },
          flex: '1 1 auto',
          width: {
            xs: '100%',
            lg: shown ? 'calc(100% - 430px)' : '100%',
          },
          minWidth: 0,
        }}
      >
        {assistantAvailable && !headerHost && <Box data-workflow-assistant-fallback sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>{toggle}</Box>}
        {assistantAvailable && headerHost && createPortal(toggle, headerHost)}
        {children}
      </Box>
      </WorkflowAssistantHeaderContext.Provider>

      {(enabled || editAvailable) && <Box
        ref={asideRef}
        id="workflow-assistant-sidebar"
        component="aside"
        aria-label="Workflow conversation"
        aria-description={workflowName || 'Current workflow'}
        data-testid="workflow-chat-companion"
        data-tour-id={shown ? 'farchat-window' : undefined}
        sx={{
          order: { xs: 1, lg: 2 },
          display: shown ? 'flex' : 'none',
          position: { xs: 'relative', lg: 'sticky' },
          top: { lg: 72 },
          width: { xs: '100%', lg: 430 },
          height: { xs: 620, lg: 'calc(100vh - 80px)' },
          // The desktop panel must fit the actual viewport. A fixed minimum
          // height made the input and Send button fall below the fold on
          // shorter laptop windows even though the aside is sticky. Narrow
          // layouts retain their intentionally tall, stacked conversation.
          minHeight: WORKFLOW_CHAT_COMPANION_MIN_HEIGHT,
          p: 0,
          minWidth: 0,
          flexDirection: 'column',
          overflow: 'hidden',
          borderBottom: { xs: 1, lg: 0 },
          borderLeft: { xs: 0, lg: 1 },
          borderColor: 'divider',
          bgcolor: 'background.default',
        }}
      >
        <Box sx={{ flex: 1, height: '100%', minHeight: 0, minWidth: 0, width: '100%' }}>
          {!authorized && editAvailable ? <EditOnlyDocumentConversation /> : authorized && <HomeChatPanel
            key={JSON.stringify([userKey, flowId, conversationRevision])}
            initialSessionId={sessionId}
            displayMode="companion"
            pageContext={pageContext}
            onActiveSessionIdChange={onSessionIdChange}
            onTurnStart={onTurnStart}
            onOpenWorkflow={onOpenWorkflow}
            onOpenMrWorkflow={onOpenMrWorkflow}
            onOpenGeneratedServicesPwsWorkflow={onOpenGeneratedServicesPwsWorkflow}
            onOpenNavigationHandle={onOpenNavigationHandle}
            onOpenCreatedWorkflow={onOpenCreatedWorkflow}
            onStartWorkflowType={onStartWorkflowType}
            onBeforeWorkflowNavigation={onBeforeWorkflowNavigation}
          />}
        </Box>
      </Box>}
    </Box>
  );
};

export default WorkflowChatCompanion;
